import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as regionApi from './api'
import type { RegionRequest, RegionUpdateRequest } from './types'

export const regionKeys = {
  active: ['regions', 'active'] as const,
  all: ['regions', 'all'] as const,
  userRegions: ['user-regions'] as const,
  regionUsers: (code: string) => ['region-users', code] as const,
}

function invalidateDict(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: regionKeys.active })
  qc.invalidateQueries({ queryKey: regionKeys.all })
}

// ----- dictionary -----

/** Active regions for dropdowns. Stała słownikowa - długi cache. */
export function useRegions() {
  return useQuery({ queryKey: regionKeys.active, queryFn: () => regionApi.listRegions(true), staleTime: Infinity })
}

/** All regions (incl. inactive) for the admin DictionaryPanel. */
export function useRegionsAdmin() {
  return useQuery({ queryKey: regionKeys.all, queryFn: () => regionApi.listRegions(false) })
}

export function useCreateRegion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: RegionRequest) => regionApi.createRegion(payload),
    onSuccess: () => invalidateDict(qc),
  })
}

export function useUpdateRegion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { code: string; payload: RegionUpdateRequest }) =>
      regionApi.updateRegion(vars.code, vars.payload),
    onSuccess: () => invalidateDict(qc),
  })
}

export function useDeleteRegion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (code: string) => regionApi.deleteRegion(code),
    onSuccess: () => invalidateDict(qc),
  })
}

export function useReorderRegions() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (codes: string[]) => regionApi.reorderRegions(codes),
    onSuccess: () => invalidateDict(qc),
  })
}

// ----- assignment -----

/** All user->region assignments, for the per-user region column. */
export function useUserRegions() {
  return useQuery({ queryKey: regionKeys.userRegions, queryFn: () => regionApi.listUserRegions() })
}

export function useRegionUsers(code: string) {
  return useQuery({ queryKey: regionKeys.regionUsers(code), queryFn: () => regionApi.listRegionUsers(code) })
}

export function useAssignUserRegion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { userId: number; regionCode: string }) =>
      regionApi.assignUserRegion(vars.userId, vars.regionCode),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: regionKeys.userRegions })
      qc.invalidateQueries({ queryKey: ['region-users'] })
    },
  })
}

export function useUnassignUserRegion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: number) => regionApi.unassignUserRegion(userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: regionKeys.userRegions })
      qc.invalidateQueries({ queryKey: ['region-users'] })
    },
  })
}
